import { z } from 'zod';

export const BOUNDARY_LIMITS = {
  maxPayloadBytes: 400 * 1024,
  maxFieldCount: 500,
  maxEnumValues: 100,
  maxRoutes: 200,
  maxActions: 300
} as const;

export const BoundaryViolationSchema = z.object({
  type: z.enum([
    'payload_size',
    'field_count',
    'enum_values',
    'route_count',
    'action_count'
  ]),
  limit: z.number(),
  actual: z.number(),
  path: z.string().optional()
});

export type BoundaryViolation = z.infer<typeof BoundaryViolationSchema>;

export function validateBoundaries(payload: unknown): BoundaryViolation[] {
  const violations: BoundaryViolation[] = [];
  const json = JSON.stringify(payload);
  const sizeBytes = Buffer.byteLength(json, 'utf8');

  if (sizeBytes > BOUNDARY_LIMITS.maxPayloadBytes) {
    violations.push({
      type: 'payload_size',
      limit: BOUNDARY_LIMITS.maxPayloadBytes,
      actual: sizeBytes
    });
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    if (Array.isArray(record.fields) && record.fields.length > BOUNDARY_LIMITS.maxFieldCount) {
      violations.push({
        type: 'field_count',
        limit: BOUNDARY_LIMITS.maxFieldCount,
        actual: record.fields.length
      });
    }

    if (Array.isArray(record.routes) && record.routes.length > BOUNDARY_LIMITS.maxRoutes) {
      violations.push({
        type: 'route_count',
        limit: BOUNDARY_LIMITS.maxRoutes,
        actual: record.routes.length
      });
    }

    if (Array.isArray(record.actions) && record.actions.length > BOUNDARY_LIMITS.maxActions) {
      violations.push({
        type: 'action_count',
        limit: BOUNDARY_LIMITS.maxActions,
        actual: record.actions.length
      });
    }

    if (Array.isArray(record.enums)) {
      record.enums.forEach((enumEntry, index) => {
        if (!enumEntry || typeof enumEntry !== 'object') {
          return;
        }
        const values = (enumEntry as { values?: unknown }).values;
        if (Array.isArray(values) && values.length > BOUNDARY_LIMITS.maxEnumValues) {
          violations.push({
            type: 'enum_values',
            limit: BOUNDARY_LIMITS.maxEnumValues,
            actual: values.length,
            path: `enums.${index}`
          });
        }
      });
    }
  }

  return violations;
}
